import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  clearCampaignData,
  setError,
  setLoading,
} from "../../../Redux/campaignSlice";
import { createCampaign } from "../../services/companyAuthService";
import { FaRegCopy } from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const LandingPages = ({ onNext, onPrevious, campaignData }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [landingPageUrl, setLandingPageUrl] = useState("");

  // Get loading state from Redux store
  const loading = useSelector((state) => state.campaign.loading);

  // Initialize landing page URL when campaign data is available
  useEffect(() => {
    if (campaignData?.landingPage) {
      setLandingPageUrl(campaignData.landingPage);
    }
  }, [campaignData?.landingPage]);

  // Determine campaign type and related UI elements
  const campaignType = campaignData?.general?.campaignType;
  const isAdvertisingCampaign = campaignType === 'Advertising';
  const isAffiliateCampaign = campaignType === 'Affiliate';
  const isHybridCampaign = campaignType === 'Hybrid';

  // For advertising campaigns, we show "Brand Info", for affiliate/hybrid we show "Landing Pages"
  const pageTitle = isAdvertisingCampaign ? "Brand Info" : "Landing Pages";
  const inputLabel = isAdvertisingCampaign ? "Brand Info URL" : "Product URL";
  const inputPlaceholder = isAdvertisingCampaign ? "https://example.com/brand-info" : "https://example.com";

  const truncateUrl = (url, maxLength = 20) => {
    return url.length > maxLength ? url.slice(0, maxLength) + "..." : url;
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Link copied to clipboard!");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate landing page URL
    if (!landingPageUrl.trim()) {
      toast.error("Please enter a valid URL");
      return;
    }

    // Call onNext to update parent component data
    if (onNext) {
      onNext(landingPageUrl);
    }

    dispatch(setLoading(true));

    try {
      const finalData = {
        ...campaignData.general,
        commission: campaignData.commission,
        landingPage: landingPageUrl,
      };

      const response = await createCampaign(finalData);

      if (response.success) {
        setSubmitted(true);
        toast.success("Campaign created successfully!");
        dispatch(clearCampaignData());
      }
    } catch (error) {
      console.error("Error creating campaign:", error);
      dispatch(setError(error.message));
      toast.error(error.message || "Failed to create campaign");
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleContinueIntegration = () => {
    navigate("/company/dashboard/affiliates/create");
  };

  return (
    <div className="w-full bg-white shadow p-4 md:p-6 rounded-md">
      <ToastContainer position="top-right" autoClose={2000} />
      {!submitted ? (
        <>
          <h3 className="text-lg font-semibold mb-4">{pageTitle}</h3>
          <form onSubmit={handleSubmit} className="space-y-6 relative">
            {loading && (
              <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded-md">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-orange-500 border-t-transparent mx-auto mb-2"></div>
                  <p className="text-orange-600 font-medium">Creating your campaign...</p>
                </div>
              </div>
            )}
            <div className="w-full">
              <label className="block text-sm font-medium text-gray-700">
                {inputLabel} <span className="text-red-500">*</span>
              </label>
              <input
                type="url"
                value={landingPageUrl}
                onChange={(e) => setLandingPageUrl(e.target.value)}
                className={`w-full border border-orange-300 rounded-md p-2 mt-1 focus:border-orange-500 focus:ring-orange-500 transition-all duration-200 ${loading ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                placeholder={inputPlaceholder}
                required
                disabled={loading}
              />
              {isAdvertisingCampaign && (
                <p className="mt-1 text-sm text-gray-500">
                  Please provide the URL where brand information is displayed for advertising purposes.
                </p>
              )}
              {isAffiliateCampaign && (
                <p className="mt-1 text-sm text-gray-500">
                  Please provide the product landing page URL where affiliates will direct their traffic.
                </p>
              )}
              {isHybridCampaign && (
                <p className="mt-1 text-sm text-gray-500">
                  Please provide the product landing page URL where affiliates will direct their traffic.
                </p>
              )}
            </div>

            <div className="flex justify-between">
              <button
                type="button"
                onClick={onPrevious}
                className={`px-4 py-2 rounded-md transition-all duration-200 ${loading
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                disabled={loading}
              >
                Previous
              </button>
              <button
                type="submit"
                disabled={loading || submitted}
                className={`px-6 py-3 rounded-md min-w-[180px] transition-colors duration-200 text-white font-medium ${loading || submitted
                    ? 'bg-orange-400 cursor-not-allowed'
                    : 'bg-orange-500 hover:bg-orange-600'
                  }`}
              >
                {loading
                  ? 'Submitting...'
                  : submitted
                    ? 'Submitted'
                    : 'Submit Campaign'}
              </button>


            </div>
          </form>
        </>
      ) : (
        <div className="text-center">
          <h3 className="text-xl font-semibold text-black">
            You've successfully created your campaign!
          </h3>
          <div className="mt-4 p-4 bg-gray-50 rounded-md">
            <h4 className="font-semibold mb-2">Campaign Details:</h4>
            <div className="text-left space-y-2">
              <p>
                <strong>Name:</strong> {campaignData.general.name}
              </p>
              <p>
                <strong>Brand:</strong> {campaignData.general.brandName || campaignData.general.brandId || 'N/A'}
              </p>
              <p>
                <strong>Campaign Type:</strong> {campaignData.general.campaignType}
              </p>
              <p>
                <strong>Commission Model:</strong>{" "}
                {campaignData.commission.model}
              </p>
              <p className="flex items-center gap-2">
                <strong>{isAdvertisingCampaign ? "Brand Info URL" : "Landing Page"}:</strong>
                <span title={landingPageUrl}>{truncateUrl(landingPageUrl)}</span>
                <FaRegCopy
                  className="cursor-pointer text-gray-600 hover:text-blue-600"
                  onClick={() => handleCopy(landingPageUrl)}
                />
              </p>
            </div>
          </div>
          <p className="mt-4 text-gray-700">
            Now, it's time to configure your tracking setup.
            <br />
            Please follow the instructions in the next steps.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4 mt-4">
            <button
              onClick={() => navigate("/company/dashboard/campaigns/")}
              className="w-48 bg-orange-500 text-white py-2 rounded-lg hover:bg-orange-600 transition"
            >
              Your Campaigns
            </button>
            <button
              onClick={()=> navigate("/company/dashboard/campaigns/advertising/create")}
              className="w-48 bg-orange-500 text-white py-2 rounded-lg hover:bg-orange-600 transition"
            >
              Campaign Advertising
            </button>
            <button
              onClick={handleContinueIntegration}
              className="w-48 bg-orange-500 text-white py-2 rounded-lg hover:bg-orange-600 transition"
            >
              Continue Integration
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPages;
